import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";

const apiBase = import.meta.env.VITE_API_URL as string;

interface LinkInfo {
  valid: boolean;
  reason: string | null;
  status: string | null;
  email: string | null;
  name: string | null;
  amountCents: number | null;
  currency: string | null;
}

function formatMoney(cents: number, currency = "usd") {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

export function OnboardingPay() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const returnStatus = searchParams.get("status");

  const [info, setInfo] = useState<LinkInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get<LinkInfo>(
          `${apiBase}/onboarding-payment/link/${token}`
        );
        if (!cancelled) setInfo(res.data);
      } catch {
        if (!cancelled) {
          setInfo({
            valid: false,
            reason: "invalid",
            status: null,
            email: null,
            name: null,
            amountCents: null,
            currency: null,
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const startCheckout = async () => {
    if (!token) return;
    try {
      setPaying(true);
      setError("");
      const res = await axios.post<{ url: string }>(
        `${apiBase}/onboarding-payment/link/${token}/checkout`
      );
      window.location.href = res.data.url;
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Could not start checkout. Please try again.";
      setError(message);
      setPaying(false);
    }
  };

  const amountDisplay =
    info?.amountCents != null
      ? formatMoney(info.amountCents, info.currency || "usd")
      : "$1,000.00";

  const reasonMessage = (() => {
    switch (info?.reason) {
      case "already_paid":
        return "This payment has already been completed. Thank you!";
      case "revoked":
        return "This payment link has been revoked. Please contact support.";
      case "expired":
        return "This payment link has expired. Please request a new one.";
      case "refunded":
        return "This payment was refunded and the link is no longer valid.";
      case "invalid":
        return "This payment link is invalid or does not exist.";
      default:
        return "This payment link is no longer valid.";
    }
  })();

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b border-border bg-card">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-lg font-semibold text-foreground">
            Reveshare
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-card border border-border rounded-2xl shadow-sm p-8">
          {loading ? (
            <div className="text-center text-muted-foreground py-8">Loading...</div>
          ) : returnStatus === "success" &&
            (info?.status === "PAID" || info?.reason === "already_paid") ? (
            <div className="text-center space-y-3">
              <h1 className="text-2xl font-semibold text-foreground">
                Payment successful
              </h1>
              <p className="text-muted-foreground">
                Thank you{info?.name ? `, ${info.name}` : ""}. Your payment of{" "}
                {amountDisplay} has been received. A confirmation email is on
                its way.
              </p>
            </div>
          ) : returnStatus === "success" && info?.valid ? (
            <div className="text-center space-y-3">
              <h1 className="text-2xl font-semibold text-foreground">
                Processing payment
              </h1>
              <p className="text-muted-foreground">
                We&apos;re confirming your payment. This page will update
                shortly — you&apos;ll also receive a confirmation email.
              </p>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="text-sm text-[#d7785b] underline"
              >
                Refresh status
              </button>
            </div>
          ) : returnStatus === "cancelled" && info?.valid ? (
            <div className="space-y-4 text-center">
              <h1 className="text-2xl font-semibold text-foreground">
                Checkout cancelled
              </h1>
              <p className="text-muted-foreground">
                No charge was made. You can try again whenever you&apos;re
                ready.
              </p>
              <button
                type="button"
                onClick={startCheckout}
                disabled={paying}
                className="w-full py-3 rounded-lg bg-[#d7785b] text-white font-medium hover:bg-[#c86c51] disabled:opacity-50"
              >
                {paying ? "Redirecting..." : `Pay ${amountDisplay}`}
              </button>
              {error && <p className="text-sm text-red-600">{error}</p>}
            </div>
          ) : !info?.valid ? (
            <div className="text-center space-y-3">
              <h1 className="text-2xl font-semibold text-foreground">
                {info?.reason === "already_paid"
                  ? "Already paid"
                  : "Link unavailable"}
              </h1>
              <p className="text-muted-foreground">{reasonMessage}</p>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="text-center space-y-2">
                <h1 className="text-2xl font-semibold text-foreground">
                  Onboarding payment
                </h1>
                <p className="text-muted-foreground text-sm">
                  One-time payment to complete your Reveshare onboarding.
                </p>
              </div>

              <div className="rounded-xl bg-muted border border-border p-4 space-y-2 text-sm">
                {info.name && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Name</span>
                    <span className="font-medium text-foreground">
                      {info.name}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Email</span>
                  <span className="font-medium text-foreground">
                    {info.email}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Amount</span>
                  <span className="font-semibold text-foreground text-lg">
                    {amountDisplay}
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={startCheckout}
                disabled={paying}
                className="w-full py-3 rounded-lg bg-[#d7785b] text-white font-medium hover:bg-[#c86c51] disabled:opacity-50"
              >
                {paying ? "Redirecting to Stripe..." : `Pay ${amountDisplay}`}
              </button>
              {error && <p className="text-sm text-red-600 text-center">{error}</p>}
              <p className="text-xs text-muted-foreground text-center">
                Secure checkout powered by Stripe. This link can only be used
                once.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
